import pkg from 'eventemitter2'
const { EventEmitter2 } = pkg

import type { EntityChangePayload } from '@gzl10/nexus-sdk'

// Payload genérico para eventos CRUD de DB
export interface DbEventPayload {
  table: string
  action: 'created' | 'updated' | 'deleted'
  data: unknown
  timestamp: Date
}

// Tipos de eventos
export interface NexusEvents {
  // Lifecycle
  'server.starting': { port: number; host: string }
  'server.started': { port: number; host: string }
  'server.stopping': undefined
  'server.stopped': undefined
  'server.restarting': undefined

  // Database connection
  'db.connected': { type: 'sqlite' | 'postgresql' | 'mysql' }
  'db.disconnected': undefined

  // Database CRUD (automático via interceptor)
  // Uso: nexusEvents.on('db.users.created', ...) o nexusEvents.on('db.*.*', ...)
  [key: `db.${string}.created`]: DbEventPayload
  [key: `db.${string}.updated`]: DbEventPayload
  [key: `db.${string}.deleted`]: DbEventPayload

  // Auth (lógica de negocio, manual)
  'auth.login': { userId: string; email: string }
  'auth.logout': { userId: string }
  'auth.refresh': { userId: string }
  'auth.failed': { email: string; reason: string }

  // Socket.IO
  'socket.initialized': undefined
  'socket.user.connected': { userId: string; roleIds: string[]; socketId: string }
  'socket.user.disconnected': { userId: string; socketId: string }

  // Notifications
  'notifications.sent': { id: string; target_type: string; target_value?: string; sent: number }

  // Entity real-time (business-level CRUD, emitted by services)
  'entity.created': EntityChangePayload
  'entity.updated': EntityChangePayload
  'entity.deleted': EntityChangePayload

  // Chat (for plugin hooks - e.g. AI bot auto-responses)
  'chat.message.created': { channelId: string; messageId: string; userId: string; body: string }
  'chat.message.deleted': { channelId: string; messageId: string; userId: string }
  'chat.channel.created': { channelId: string; type: 'dm' | 'group'; createdBy: string }
  'chat.member.joined': { channelId: string; userId: string; role: string }
  'chat.member.left': { channelId: string; userId: string }

  // Audit (consumed by audit module if loaded, silently dropped otherwise)
  'audit.log': {
    source: string
    action: string
    actorId?: string
    actorEmail?: string
    resourceType?: string
    resourceId?: string
    ip?: string
    userAgent?: string
    metadata?: Record<string, unknown>
  }
}

// Tipo helper para listeners tipados
export type NexusEventName = keyof NexusEvents
export type NexusEventPayload<T extends NexusEventName> = NexusEvents[T]

// Emitter singleton
class TypedEventEmitter extends EventEmitter2 {
  emitEvent<T extends NexusEventName>(
    event: T,
    ...args: NexusEvents[T] extends undefined ? [] : [NexusEvents[T]]
  ): boolean {
    return this.emit(event, ...args)
  }

  onEvent<T extends NexusEventName>(
    event: T,
    listener: NexusEvents[T] extends undefined
      ? () => void
      : (payload: NexusEvents[T]) => void
  ): this {
    this.on(event, listener as (...args: unknown[]) => void)
    return this
  }

  onceEvent<T extends NexusEventName>(
    event: T,
    listener: NexusEvents[T] extends undefined
      ? () => void
      : (payload: NexusEvents[T]) => void
  ): this {
    this.once(event, listener as (...args: unknown[]) => void)
    return this
  }

  offEvent<T extends NexusEventName>(
    event: T,
    listener: NexusEvents[T] extends undefined
      ? () => void
      : (payload: NexusEvents[T]) => void
  ): this {
    this.off(event, listener as (...args: unknown[]) => void)
    return this
  }
}

export const nexusEvents = new TypedEventEmitter({
  wildcard: true,
  delimiter: '.',
  maxListeners: 50,
  verboseMemoryLeak: true
})

// Re-export para uso directo
export { TypedEventEmitter }
